/**
 * MovingPlatform: a static rectangular collider that follows a polyline path.
 *
 * Implementation notes
 *  - We keep `isStatic: true` so the collision solver treats the body as
 *    immovable (the player bounces off correctly). The body is teleported
 *    each tick via `Body.setPosition`, and we set `body.velocity` manually
 *    so Matter's friction / restitution math sees the moving frame.
 *  - The visual is a Pixi `Graphics` rect that follows the body each render
 *    frame via the same code path the renderer uses for static platforms.
 *  - A faint dotted line traces the path so authors / players can see where
 *    it's headed (rendered once into a separate `pathView` Graphics).
 */

import Matter from 'matter-js';
import { Graphics } from 'pixi.js';
import type { PathSpec } from '../Path.js';
import { pathPosition } from '../Path.js';

export interface MovingPlatformOptions {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Optional rotation (degrees) applied at build time. The body keeps its
   *  rotation while moving — paths translate, they don't spin. */
  angle?: number;
  bouncy?: boolean;
  path: PathSpec;
}

export class MovingPlatform {
  readonly body: Matter.Body;
  readonly view: Graphics;
  readonly pathView: Graphics;
  private readonly path: PathSpec;
  private readonly halfW: number;
  private readonly halfH: number;
  private readonly originOffsetX: number;
  private readonly originOffsetY: number;
  private prevX: number;
  private prevY: number;

  constructor(opts: MovingPlatformOptions) {
    this.path = opts.path;
    this.halfW = opts.w / 2;
    this.halfH = opts.h / 2;
    // The (x,y) in the schema is the top-left at t=0. The path's first point
    // is treated as the body's CENTER position, so subsequent path samples
    // place the body's center directly. We capture the offset between the
    // schema's top-left and the path's first waypoint so authoring stays
    // intuitive: "drag the platform to (x,y), it then orbits around there".
    this.originOffsetX = opts.x + this.halfW - opts.path.points[0]!.x;
    this.originOffsetY = opts.y + this.halfH - opts.path.points[0]!.y;

    const start = pathPosition(opts.path, 0);
    const cx = start.x + this.originOffsetX;
    const cy = start.y + this.originOffsetY;
    const angleRad = ((opts.angle ?? 0) * Math.PI) / 180;
    const restitution = opts.bouncy ? 0.85 : 0.35;
    this.body = Matter.Bodies.rectangle(cx, cy, opts.w, opts.h, {
      isStatic: true,
      friction: 0.05,
      frictionStatic: 0.05,
      restitution,
      angle: angleRad,
      label: 'platform',
    });
    this.prevX = cx;
    this.prevY = cy;

    // Visual matches static Platform's look but uses a slightly different
    // accent so authors / players can read "this thing moves" at a glance.
    const g = new Graphics();
    const fill = opts.bouncy ? 0x4a88ff : 0x2a1a4a;
    const stroke = opts.bouncy
      ? { color: 0xffffff, width: 2, alpha: 0.7 }
      : { color: 0xaa77dd, width: 2, alpha: 0.6 };
    g.roundRect(-this.halfW, -this.halfH, opts.w, opts.h, Math.min(this.halfH, 12))
      .fill(fill)
      .stroke(stroke);
    g.x = cx;
    g.y = cy;
    g.rotation = angleRad;
    this.view = g;

    // Dotted breadcrumb of the path.
    const pv = new Graphics();
    const pts = opts.path.points;
    if (pts.length >= 2) {
      // Draw 4-pixel dashes with 6-pixel gaps along each segment.
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1]!, b = pts[i]!;
        const dx = b.x - a.x, dy = b.y - a.y;
        const L = Math.hypot(dx, dy);
        if (L < 0.001) continue;
        const ux = dx / L, uy = dy / L;
        const period = 10;
        let t = 0;
        while (t < L) {
          const t2 = Math.min(t + 4, L);
          pv.moveTo(a.x + ux * t + this.originOffsetX, a.y + uy * t + this.originOffsetY)
            .lineTo(a.x + ux * t2 + this.originOffsetX, a.y + uy * t2 + this.originOffsetY)
            .stroke({ color: 0xaa77dd, width: 2, alpha: 0.35 });
          t += period;
        }
      }
      // Tiny waypoint dots.
      for (const p of pts) {
        pv.circle(p.x + this.originOffsetX, p.y + this.originOffsetY, 2.5)
          .fill({ color: 0xddbbff, alpha: 0.45 });
      }
    }
    this.pathView = pv;
  }

  /** Advance the body to its position at level-time `tSec`. */
  step(tSec: number, dtSec: number): void {
    const p = pathPosition(this.path, tSec);
    const cx = p.x + this.originOffsetX;
    const cy = p.y + this.originOffsetY;
    Matter.Body.setPosition(this.body, { x: cx, y: cy });
    // Synthesize a velocity from the discrete delta so Matter's contact
    // resolution sees the motion (matters mostly for restitution handoff).
    const vx = dtSec > 0 ? (cx - this.prevX) / dtSec : 0;
    const vy = dtSec > 0 ? (cy - this.prevY) / dtSec : 0;
    Matter.Body.setVelocity(this.body, { x: vx, y: vy });
    this.prevX = cx;
    this.prevY = cy;
    // Keep the visual glued to the body — the renderer doesn't auto-sync
    // static bodies because they're assumed not to move.
    this.view.x = cx;
    this.view.y = cy;
  }
}
